import { useDocumentTitle } from "usehooks-ts";
import {
  DataTableContextProvider,
  useDataTable,
} from "./DataTable/DataTableContext";
import { DataTable } from "./DataTable/DataTable";
import { useEffect, useState } from "react";
import { US_STATES } from "../utils";
import { Link, useSearchParams } from "react-router-dom";
import { FilterMethods } from "./DataTable/types";
import { ColumnInstance } from "react-table";
import { TableHeaderFilterField } from "./DataTable/TableHeaderFilterField";

const statusFilter: FilterMethods[string] = (rows, columnId, filterValue) =>
  rows.filter((row) => {
    const value = row.values[columnId[0]] as string;
    if (filterValue === "") return true;
    else if (filterValue === "-")
      return value !== "DOE" && value !== "Parochial";
    else return value === filterValue;
  });

const DOEFilter: React.FC<{ column: ColumnInstance }> = ({ column }) => {
  return (
    <select
      value={column.filterValue || ""}
      onChange={(e) => {
        e.preventDefault();
        column.setFilter(e.target.value);
      }}
      className="h-8 w-[100px] rounded px-2 py-0 text-sm text-dm-charcoal-600"
    >
      <option value="">All</option>
      <option value="DOE">DOE</option>
      <option value="Parochial">Parochial</option>
      <option value="-">Neither</option>
    </select>
  );
};

export default function Quote() {
  useDocumentTitle("School/Dist Search");

  return (
    <DataTableContextProvider
      source="/manager_new/manage/search-school-or-district"
      defaults={{ sortBy: [], filterBy: [] }}
      sortable={["type", "name", "level", "students"]}
      sortSource="client"
      filterable={["DOE", "name", "level", "id"]}
      filterSource="client"
      filterOverrides={{
        DOE: DOEFilter,
        level: ({ column }) => (
          <TableHeaderFilterField column={column} className="w-20" />
        ),
      }}
      filterMethods={{
        DOE: statusFilter,
      }}
      downloadButton={false}
      columnOverrides={{
        view: ({ value }) => (
          <Link to={value} aria-label="View school or district">
            <i className="far fa-eye" aria-hidden="true"></i>
          </Link>
        ),
        address: ({ value }) => (
          <>
            <div>{value.street}</div>
            <div>
              {value.city}
              {", "}
              {value.state} {value.zip}
            </div>
          </>
        ),
      }}
      showCustomErrorMessage
    >
      <QuoteContent />
    </DataTableContextProvider>
  );
}

const QuoteContent = () => {
  const { setExternalFilterBy } = useDataTable();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stateSearch, setStateSearch] = useState(
    searchParams.get("state") || "NY"
  );
  const [schoolSearch, setSchoolSearch] = useState(
    searchParams.get("school") || ""
  );

  useEffect(() => {
    if (schoolSearch && stateSearch) {
      setExternalFilterBy([
        { id: "state", value: stateSearch },
        { id: "search", value: schoolSearch },
      ]);
    }
    // if search params exist on initial load (a refresh or back button, for example), apply them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="flex flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-gray-900">
        School / District Search
      </h1>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setExternalFilterBy([
            { id: "state", value: stateSearch },
            { id: "search", value: schoolSearch },
          ]);
          setSearchParams({
            state: stateSearch,
            school: schoolSearch,
          });
        }}
        className="grid grid-cols-1 rounded bg-white px-4 py-5 shadow sm:p-6 md:grid-cols-8"
      >
        <input
          type="text"
          id="school-search"
          name="search"
          placeholder="District or School Name"
          value={schoolSearch || ""}
          onChange={(e) => setSchoolSearch(e.target.value)}
          className="relative col-span-1 block border-gray-300 focus:z-10 focus:border-dm-brand-blue-500 focus:outline-offset-1 focus:ring-dm-brand-blue-500 md:col-span-2 md:rounded-l"
        />
        <select
          id="state"
          name="state"
          placeholder="State"
          required
          onChange={(e) => {
            setStateSearch(e.target.value);
          }}
          value={stateSearch}
          className="relative -mt-px block border-gray-300 text-sm focus:z-10 focus:border-dm-brand-blue-500 focus:outline-offset-1 focus:ring-dm-brand-blue-500 md:-ml-px md:-mt-0"
        >
          <option disabled value="-">
            Select State
          </option>
          {Object.entries(US_STATES).map(([key, name]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="relative -mt-px items-center space-x-2 border border-gray-300 bg-gray-50 px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 focus:z-10 focus:border-dm-brand-blue-500 focus:outline-none focus:outline-offset-1 focus:ring-1 focus:ring-dm-brand-blue-500 md:-ml-px md:-mt-0 md:rounded-r"
        >
          Search
        </button>
      </form>
      <DataTable />
    </main>
  );
};
